import {
	createTheme,
	defaultVariantColorsResolver,
	parseThemeColor,
	VariantColorsResolver,
} from '@mantine/core';

const variantColorResolver: VariantColorsResolver = (input) => {
	const defaultResolvedColors = defaultVariantColorsResolver(input);
	const parsedColor = parseThemeColor({
		color: input.color || input.theme.primaryColor,
		theme: input.theme,
	});

	if (parsedColor.isThemeColor) {
		if (input.variant === 'filled' && input.color === 'red') {
			return {
				...defaultResolvedColors,
				color: 'var(--mantine-color-white)',
				background: 'var(--mantine-color-red-2)',
			};
		}

		if (input.variant === 'filled' && input.color === 'orange') {
			return {
				...defaultResolvedColors,
				color: 'var(--mantine-color-white)',
				background: 'var(--mantine-color-orange-2)',
			};
		}

		if (input.variant === 'filled' && input.color === 'blue') {
			return {
				...defaultResolvedColors,
				color: 'var(--mantine-color-white)',
				background: 'var(--mantine-color-blue-4)',
			};
		}

		if (input.variant === 'filled') {
			return {
				...defaultResolvedColors,
				color: 'var(--mantine-color-black)',
				background: 'var(--mantine-color-green-1)',
			};
		}

		if (input.variant === 'light') {
			return {
				...defaultResolvedColors,
				color: 'var(--mantine-color-green-1)',
				hoverColor: 'var(--mantine-color-green-1)',
				hover: 'var(--mantine-color-green-0)',
				background: 'var(--mantine-color-green-0)',
			};
		}

		if (input.variant === 'transparent') {
			return {
				...defaultResolvedColors,
				color: 'var(--mantine-color-green-1)',
				hoverColor: 'var(--mantine-color-green-1)',
				hover: 'white',
				background: 'white',
			};
		}

		if (input.variant === 'danger') {
			return {
				...defaultResolvedColors,
				color: 'var(--mantine-color-red-2)',
				hoverColor: 'var(--mantine-color-red-2)',
				hover: 'white',
				background: 'white',
			};
		}

		if (input.variant === 'subtle') {
			return {
				...defaultResolvedColors,
				color: 'var(--mantine-color-gray-0)',
				hoverColor: 'var(--mantine-color-gray-0)',
				// hover: 'white',
				// background: 'white',
			};
		}
	}

	return defaultResolvedColors;
};

export const theme = createTheme({
	components: {
		Accordion: {
			styles: {
				label: {
					fontSize: '16px',
					fontWeight: 700,
				},
				content: {
					fontSize: '14px',
					fontWeight: 400,
					lineHeight: '20.3px',
				},
				root: {
					width: '100%',
				},
			},
		},
		Alert: {
			styles: {
				title: {
					fontSize: '1.1rem',
					marginTop: '-4px',
				},
				message: {
					lineHeight: '20px',
				},
			},
		},
		Anchor: {
			styles: {
				root: {
					fontSize: '16px',
					fontWeight: 400,
					textDecoration: 'underline',
					color: 'var(--mantine-color-blue-9)',
				},
			},
		},
		Button: {
			styles: {
				root: {
					innerHeight: '56px',
					borderRadius: '9999px',
				},
			},
			defaultProps: {
				variant: 'light',
				color: 'green.0',
				fw: '900',
			},
		},
		TextInput: {
			styles: {
				root: {
					fontFamily: 'DM Sans',
					width: '100%',
				},
				label: {
					fontFamily: 'DM Sans',
					fontWeight: '500',
					fontSize: '14px',
				},
			},
		},
		Notification: {
			styles: {
				title: {
					color: 'var(--mantine-color-white)',
					fontWeight: '700',
				},
				closeButton: {
					color: 'var(--mantine-color-white)',
				},
				description: {
					color: 'var(--mantine-color-white)',
				},
			},
		},
		InputWrapper: {
			styles: {
				label: {
					fontFamily: 'DM Sans',
					fontWeight: '700',
				},
			},
		},
		NumberInput: {
			styles: {
				root: {
					fontFamily: 'DM Sans',
					width: '100%',
				},
				label: {
					fontFamily: 'DM Sans',
					fontWeight: '700',
				},
			},
		},
		PasswordInput: {
			styles: {
				root: {
					width: '100%',
				},
				label: {
					fontFamily: 'DM Sans',
					fontWeight: '700',
				},
			},
		},
		Select: {
			styles: {
				root: {
					width: '100%',
				},
				dropdown: {
					zIndex: 1000,
				},
			},
		},
		Textarea: {
			styles: {
				root: {
					width: '100%',
				},
			},
		},
		Switch: {
			styles: {
				body: {
					alignItems: 'center',
					justifyContent: 'space-between',
				},
				label: {
					fontFamily: 'DM Sans',
					fontWeight: '700',
				},
			},
		},
		Carousel: {
			styles: {
				indicator: {
					backgroundColor: 'var(--mantine-color-blue-0)',
					height: '16px',
					width: '16px',
				},
				indicators: {
					gap: '36px',
				},
				viewport: {
					height: '100%',
					width: '100%',
				},
				root: {
					height: '100%',
					width: '100%',
					flex: 1,
				},
				slide: {
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
				},
			},
		},
	},
	fontFamily: 'Goldplay',
	fontSizes: {
		sm: '16px',
		md: '20px',
		lg: '24px',
		xl: '36px',
	},
	lineHeights: {
		md: '30px',
	},
	headings: {
		sizes: {
			h1: { fontWeight: '900', fontSize: '36px', lineHeight: '47.88px' },
		},
	},
	colors: {
		green: [
			'#EEF9FB',
			'#75E093',
			'#75E0934D',
			'#75E093',
			'#75E093',
			'#75E093',
			'#75E093',
			'#75E093',
			'#75E093',
			'#75E093',
		],
		blue: [
			'#C7E8E9',
			'#C7E8E9',
			'#C7E8E9',
			'#C7E8E9',
			'#5474b4',
			'#F1FBFC',
			'#1F7280',
			'#91E0ED',
			'#04234F',
			'#1A2C48',
		],
		red: [
			'#FF56564D',
			'#FFA0564D',
			'#FF5656',
			'#F24822',
			'#FF5656',
			'#FF5656',
			'#FF5656',
			'#FF5656',
			'#FF5656',
			'#FF5656',
		],
		orange: [
			'#FF923D',
			'#FF923D',
			'#FF923D',
			'#FF923D',
			'#FF923D',
			'#FF923D',
			'#FF923D',
			'#FF923D',
			'#FF923D',
			'#FF923D',
		],
		gray: [
			'#8998AF',
			'#F5F5F5',
			'#868E93',
			'#F5F5F5',
			'#E7E8E9',
			'#ADB5BD',
			'#ADB5BD',
			'#F5F5F5',
			'#F5F5F5',
			'#F5F5F5',
		],
	},
	variantColorResolver,
});
